import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getAuthData, setAuthData, clearAuthData, isTokenExpired, checkOAuthCallback } from '../utils/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    user: null,
    isAuthenticated: false,
    loading: true
  });

  // Initialize auth state only once
  useEffect(() => {
    let isMounted = true; // Prevent state updates if component unmounts

    const initializeAuth = async () => {
      try {
        // First check for OAuth callback in URL fragment
        const oauthCallback = checkOAuthCallback();
        if (oauthCallback) {
          // Handle OAuth callback - token and user came from URL fragment
          setAuthData(oauthCallback.token, oauthCallback.user);
          if (isMounted) {
            setAuthState({
              user: oauthCallback.user,
              isAuthenticated: true,
              loading: false
            });
          }
          return;
        }

        // If no OAuth callback, check for existing auth data in localStorage
        const authData = getAuthData();
        
        if (authData.token && !isTokenExpired(authData.token)) {
          if (isMounted) {
            setAuthState({
              user: authData.user,
              isAuthenticated: true,
              loading: false
            });
          }
        } else {
          clearAuthData();
          if (isMounted) {
            setAuthState({
              user: null,
              isAuthenticated: false,
              loading: false
            });
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (isMounted) {
          setAuthState({
            user: null,
            isAuthenticated: false,
            loading: false
          });
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false; // Cleanup
    };
  }, []); // Empty dependency array - runs only once

  const login = useCallback((userData, token) => {
    setAuthData(token, userData);
    setAuthState({
      user: userData,
      isAuthenticated: true,
      loading: false
    });
  }, []);

  const logout = useCallback(() => {
    clearAuthData();
    setAuthState({
      user: null,
      isAuthenticated: false,
      loading: false
    });
  }, []);

 const value = useMemo(() => ({
    ...authState,
    login,
    logout
  }), [authState, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
